import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from 'react';
import { Check, RotateCcw } from 'lucide-react';

type SignaturePadProps = {
  onSave: (signatureData: string) => void;
  onCancel?: () => void;
  width?: number;
  height?: number;
  penColor?: string;
  backgroundColor?: string;
  className?: string;
  disabled?: boolean;
};

// Canvas defaults match the SWPPP PDF signature slot (2.0" x 0.5" → 4:1).
// Keeping the draw-area ratio identical to the report slot means the image
// isn't stretched when embedded, and the inspector's baseline can anchor
// against a guide line that mirrors where the PDF's signature rule renders.
export function SignaturePad({
  onSave,
  onCancel,
  width = 400,
  height = 100,
  penColor = '#000000',
  backgroundColor = '#ffffff',
  className = '',
  disabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  // The pad is never wider than the room it is given: at a fixed 400 px it
  // hung off the card and scrolled the page sideways on a phone (Gate 7,
  // 2026-10-04). Measured once — resizing a canvas wipes what is drawn on it.
  const [padWidth, setPadWidth] = useState(width);
  useLayoutEffect(() => {
    setPadWidth(Math.min(width, wrapRef.current?.clientWidth || width));
  }, [width]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size for high DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = padWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${padWidth}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, padWidth, height);

    // Set drawing style
    ctx.strokeStyle = penColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [padWidth, height, penColor, backgroundColor]);

  // Get position from mouse/touch event
  const getPosition = useCallback(
    (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      if ('touches' in e) {
        const touch = e.touches[0];
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
      }

      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    []
  );

  // Start drawing
  const handleStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;
      e.preventDefault();

      const pos = getPosition(e);
      lastPoint.current = pos;
      setIsDrawing(true);
    },
    [disabled, getPosition]
  );

  // Draw line
  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing || disabled) return;
      e.preventDefault();

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || !lastPoint.current) return;

      const pos = getPosition(e);

      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();

      lastPoint.current = pos;
      setHasSignature(true);
    },
    [isDrawing, disabled, getPosition]
  );

  // Stop drawing
  const handleEnd = useCallback(() => {
    setIsDrawing(false);
    lastPoint.current = null;
  }, []);

  // Clear canvas
  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasSignature(false);
  }, [backgroundColor]);

  // Save signature
  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;

    // Get PNG data as base64
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  }, [hasSignature, onSave]);

  return (
    <div ref={wrapRef} className={`flex flex-col gap-3 ${className}`}>
      {/* Canvas */}
      <div
        data-testid="signature-pad-box"
        className={`relative border-2 rounded-lg overflow-hidden ${
          disabled
            ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800'
            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900'
        }`}
        style={{ width: padWidth, height }}
      >
        <canvas
          ref={canvasRef}
          className={`touch-none ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />

        {/* Baseline guide — CSS overlay, never painted into the saved PNG.
            Sits at the bottom edge of the canvas because the PDF's signature
            rule is rendered via LINEBELOW on a VALIGN=BOTTOM image cell, so
            the bottom pixel of the canvas maps to the PDF signature line. */}
        <div
          aria-hidden
          className="absolute left-3 right-3 bottom-2 border-b border-dashed border-slate-300 dark:border-slate-600 pointer-events-none"
        />

        {/* Placeholder text */}
        {!hasSignature && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-slate-400 dark:text-slate-500 text-sm">
              Sign above the line
            </span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
          >
            Cancel
          </button>
        )}

        <button
          type="button"
          onClick={handleClear}
          disabled={!hasSignature || disabled}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RotateCcw className="w-4 h-4" />
          Clear
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={!hasSignature || disabled}
          className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="w-4 h-4" />
          Save Signature
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;
